import { createSlice, Draft, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  FileSizeError,
  Medium,
  unwrapApiErrorDetails
} from '@ianesthesie-kit/services';
import { createAsyncThunkWithErrorDetailsSerializer } from '@ianesthesie-kit/redux';
import {
  Answer,
  AssignedQuestionnaire,
  AutocompleteQuestionOption,
  FixedChoices,
  isPendingStatus,
  isUploadQuestion,
  Question,
  QuestionnaireQuestionResponse,
  QuestionnaireStatus
} from './model';
import {
  FetchAssignedQuestionnaireParams,
  FetchAutocompleteQuestionOptionsParams,
  GroupAnswerDataElement,
  QuestionnaireService,
  SkipOptions
} from './questionnaire.service';
import type { RootState } from '@/store';
import { patientBffApi } from '@/modules/baseApi';
import { SentryService } from '@ianesthesie-kit/sentry';

export type InitializationStatus =
  | 'notInitialized'
  | 'initializing'
  | 'initialized'
  | 'error';

export interface InitializableState {
  initializationStatus: InitializationStatus;
}

export const INITIALIZING_ERROR_SIGNAL = 'initializing';
export const isInitializingErrorSignal = (err: any): boolean =>
  err?.message === INITIALIZING_ERROR_SIGNAL;

function pendingQuestionnaireOrThrow(thunkApi: any): AssignedQuestionnaire {
  const questionnaire = (thunkApi.getState() as RootState).questionnaire
    .assignedQuestionnaire;
  let err: Error | undefined;
  if (!questionnaire) {
    err = new Error('No current assigned questionnaire is present');
  } else if (!isPendingStatus(questionnaire.status)) {
    err = new Error(
      `Current assigned questionnaire status is ${questionnaire.status}`
    );
  }

  if (err) {
    SentryService.getInstance().captureException(err);
    throw err;
  }

  return questionnaire!;
}

function invalidateQuestionnairesCacheIfCompleted(
  resp: QuestionnaireQuestionResponse,
  dispatch: any
) {
  if (resp.questionnaireStatus === QuestionnaireStatus.COMPLETED) {
    dispatch(questionnaireApi.util.invalidateTags(['AssignedQuestionnaires']));
  }

  return resp;
}

type AnswerArg = {
  question: Question;
  value: Answer['value'] | Array<Medium | File>;
};

const notLoadingOrThrow = (state: RootState) => {
  if (state.questionnaire.loading) {
    throw new Error('Another request is already in progress');
  }
};

export const answerQuestion = createAsyncThunkWithErrorDetailsSerializer<
  AnswerArg,
  QuestionnaireQuestionResponse
>('questionnaire/answer', ({ question, value }: AnswerArg, thunkApi) => {
  notLoadingOrThrow(thunkApi.getState() as RootState);
  thunkApi.dispatch(questionnaireSlice.actions.setDirection('forwards'));
  thunkApi.dispatch(questionnaireSlice.actions.setLoading(true));

  const assignedQuestionnaire = pendingQuestionnaireOrThrow(thunkApi);
  const service = QuestionnaireService.getInstance();
  let answerPromise;
  if (isUploadQuestion(question)) {
    thunkApi.dispatch(questionnaireSlice.actions.setUploadProgress(0));
    const onProgress = ({ loaded, total }: ProgressEvent) => {
      thunkApi.dispatch(
        questionnaireSlice.actions.setUploadProgress(loaded / total)
      );
    };

    answerPromise = service
      .answerUploadQuestion(
        assignedQuestionnaire.id,
        question,
        value as Array<Medium | File>,
        onProgress
      )
      .catch((err) => {
        if (err instanceof FileSizeError) {
          throw thunkApi.rejectWithValue({
            name: err.name,
            type: err.type,
            fileName: err.fileName
          });
        }
        throw err;
      })
      .finally(() => {
        thunkApi.dispatch(
          questionnaireSlice.actions.setUploadProgress(undefined)
        );
      });
  } else {
    answerPromise = service.answerQuestion(
      assignedQuestionnaire.id,
      question,
      value as Answer['value']
    );
  }

  return answerPromise
    .then((resp) =>
      invalidateQuestionnairesCacheIfCompleted(resp, thunkApi.dispatch)
    )
    .finally(() => {
      thunkApi.dispatch(questionnaireSlice.actions.setLoading(false));
    });
});

export const answerQuestionGroup = createAsyncThunkWithErrorDetailsSerializer<
  GroupAnswerDataElement[],
  QuestionnaireQuestionResponse
>('questionnaire/answer-group', (answerData, thunkApi) => {
  const state = thunkApi.getState() as RootState;
  notLoadingOrThrow(state);
  thunkApi.dispatch(questionnaireSlice.actions.setDirection('forwards'));
  thunkApi.dispatch(questionnaireSlice.actions.setLoading(true));
  const groupName = state.questionnaire.group;
  if (!groupName) {
    throw new Error('No group name is present');
  }

  const assignedQuestionnaire = pendingQuestionnaireOrThrow(thunkApi);

  return QuestionnaireService.getInstance()
    .answerQuestion(assignedQuestionnaire.id, answerData)
    .then((resp) =>
      invalidateQuestionnairesCacheIfCompleted(resp, thunkApi.dispatch)
    )
    .finally(() => {
      thunkApi.dispatch(questionnaireSlice.actions.setLoading(false));
    });
});

type SkipPayload = Question | { question: Question; skipOptions?: SkipOptions };

export const skipQuestion = createAsyncThunkWithErrorDetailsSerializer<
  SkipPayload,
  QuestionnaireQuestionResponse
>('questionnaire/skip', (payload: SkipPayload, thunkApi) => {
  const question =
    'question' in payload ? (payload.question as Question) : payload;
  notLoadingOrThrow(thunkApi.getState() as RootState);
  thunkApi.dispatch(questionnaireSlice.actions.setDirection('forwards'));
  thunkApi.dispatch(questionnaireSlice.actions.setLoading(true));
  const assignedQuestionnaire = pendingQuestionnaireOrThrow(thunkApi);
  const skipOptions =
    'skipOptions' in payload ? payload.skipOptions : undefined;

  return QuestionnaireService.getInstance()
    .skipQuestion(assignedQuestionnaire.id, question, skipOptions)
    .then((resp) =>
      invalidateQuestionnairesCacheIfCompleted(resp, thunkApi.dispatch)
    )
    .finally(() => {
      thunkApi.dispatch(questionnaireSlice.actions.setLoading(false));
    });
});

export const back = createAsyncThunkWithErrorDetailsSerializer<
  void,
  QuestionnaireQuestionResponse
>('questionnaire/back', (_: void, thunkApi) => {
  notLoadingOrThrow(thunkApi.getState() as RootState);
  thunkApi.dispatch(questionnaireSlice.actions.setDirection('backwards'));
  thunkApi.dispatch(questionnaireSlice.actions.setLoading(true));
  const assignedQuestionnaire = pendingQuestionnaireOrThrow(thunkApi);

  return QuestionnaireService.getInstance()
    .previousQuestion(assignedQuestionnaire.id)
    .finally(() => {
      thunkApi.dispatch(questionnaireSlice.actions.setLoading(false));
    });
});

export const initialize = createAsyncThunkWithErrorDetailsSerializer<
  AssignedQuestionnaire,
  QuestionnaireQuestionResponse & {
    fixedChoices: FixedChoices;
  }
>(
  'questionnaire/init',
  async (assignedQuestionnaire: AssignedQuestionnaire, thunkApi) => {
    const qState = (thunkApi.getState() as RootState).questionnaire; // TODO infer State type
    const initStatus = qState.initializationStatus;
    if (!assignedQuestionnaire?.id) {
      throw new Error('No assigned questionnaire selected');
    }

    if (initStatus === 'initializing') {
      throw INITIALIZING_ERROR_SIGNAL;
    }

    if (initStatus !== 'notInitialized') {
      throw new Error('Questionnaire already initialized');
    }

    const dispatch = thunkApi.dispatch;
    let fixedChoices: FixedChoices;

    const { setInitializationStatus } = questionnaireSlice.actions;
    const qService = QuestionnaireService.getInstance();
    dispatch(setInitializationStatus('initializing'));

    return qService
      .fetchFixedChoices()
      .then((choices) => {
        fixedChoices = choices;
      })
      .then(() => qService.currentQuestion(assignedQuestionnaire.id))
      .then((current) => ({
        ...current,
        fixedChoices
      })) as Promise<
      QuestionnaireQuestionResponse & {
        fixedChoices: FixedChoices;
      }
    >;
  }
);

export const questionnaireApi = patientBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    assignedQuestionnaires: builder.query<
      AssignedQuestionnaire[],
      FetchAssignedQuestionnaireParams | void | undefined
    >({
      providesTags: ['AssignedQuestionnaires'],
      queryFn: (arg) =>
        QuestionnaireService.getInstance()
          .fetchAssignedQuestionnaires(arg ?? undefined)
          .then((resp) => ({
            data: resp.data
          }))
          .catch((err) => ({
            error: unwrapApiErrorDetails(err) || err
          }))
    }),

    autocompleteQuestionOptions: builder.query<
      AutocompleteQuestionOption[],
      FetchAutocompleteQuestionOptionsParams
    >({
      providesTags: (res, err, { questionId }) => [
        { type: 'AutocompleteOptions', id: questionId }
      ],
      queryFn: (arg) =>
        QuestionnaireService.getInstance()
          .fetchAutocompleteQuestionOptions(arg)
          .then((data) => ({
            data
          }))
          .catch((err) => ({
            error: unwrapApiErrorDetails(err) || err
          }))
    })
  })
});

export const {
  useAssignedQuestionnairesQuery: useAssignedQuestionnaires,
  useAutocompleteQuestionOptionsQuery: useAutocompleteQuestionOptions
} = questionnaireApi;

type CurrentQuestionData = {
  question: Question;
  answer?: Answer;
}[];

export type QuestionnaireDirection = 'forwards' | 'backwards';

export type GroupAnswerMemberState = {
  questionId: Question['id'];
  answerValue?: Answer['value'];
  canSubmit: boolean;
};

interface QuestionnaireState extends InitializableState {
  assignedQuestionnaire?: AssignedQuestionnaire;
  current: CurrentQuestionData | null;
  group?: string;
  groupTitleId?: string;
  currentGroupState?: Record<Question['id'], GroupAnswerMemberState>;
  orderIndex: number;
  totalQuestions: number;
  direction: QuestionnaireDirection;
  loading: boolean;
  fixedChoices?: FixedChoices;
  status?: QuestionnaireStatus;
  uploadProgress?: number;
}

const initialState: QuestionnaireState = {
  current: null,
  totalQuestions: 0,
  orderIndex: 0,
  direction: 'forwards',
  loading: false,
  initializationStatus: 'notInitialized'
};

export const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    setInitializationStatus: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<InitializationStatus>
    ) => {
      state.initializationStatus = action.payload;
    },
    setCurrentAssignedQuestionnaire: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<AssignedQuestionnaire | undefined>
    ) => {
      state.assignedQuestionnaire = action.payload;
    },
    setLoading: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<boolean>
    ) => {
      state.loading = action.payload;
    },
    setQuestionnaireStatus: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<QuestionnaireStatus>
    ) => {
      state.status = action.payload;
    },
    setDirection: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<QuestionnaireDirection>
    ) => {
      state.direction = action.payload;
    },
    setFixedChoices: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<FixedChoices>
    ) => {
      state.fixedChoices = action.payload;
    },
    setCurrentQuestion: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<CurrentQuestionData>
    ) => {
      state.current = action.payload;
    },
    updateGroupMemberState: (
      state,
      action: PayloadAction<GroupAnswerMemberState>
    ) => {
      if (!state.currentGroupState) {
        state.currentGroupState = {};
      }
      state.currentGroupState[action.payload.questionId] = action.payload;
    },
    setTotalQuestions: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<number>
    ) => {
      state.totalQuestions = action.payload;
    },
    setUploadProgress: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<number | undefined>
    ) => {
      state.uploadProgress = action.payload;
    },
    resetQuestionnaireState: () => initialState,
    resetAndSetQuestionnaire: (
      state: Draft<QuestionnaireState>,
      action: PayloadAction<AssignedQuestionnaire>
    ) =>
      // Atomically reset state and set new questionnaire to avoid intermediate undefined state
      ({
        ...initialState,
        assignedQuestionnaire: action.payload
      })
  },
  extraReducers: (builder) => {
    builder.addCase(initialize.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(initialize.fulfilled, (state, action) => {
      state.initializationStatus = 'initialized';
      state.assignedQuestionnaire = action.meta.arg;
      state.fixedChoices = action.payload.fixedChoices;
      updateStateWithResponse(state, action.payload);
    });

    builder.addCase(initialize.rejected, (state, action) => {
      if (!isInitializingErrorSignal(action.error)) {
        state.loading = false;
        state.initializationStatus = 'error';
      }
    });

    builder.addMatcher(
      isAnyOf(
        answerQuestion.fulfilled,
        answerQuestionGroup.fulfilled,
        skipQuestion.fulfilled,
        back.fulfilled
      ),
      (state, action) => {
        updateStateWithResponse(state, action.payload);
      }
    );

    builder.addMatcher(
      questionnaireApi.endpoints.assignedQuestionnaires.matchFulfilled,
      (state, action) => {
        const currentQuestionnaire = state.assignedQuestionnaire;
        const data = action.payload;
        if (data && currentQuestionnaire) {
          const selectedFromData = data?.find(
            (q) => q.id === currentQuestionnaire.id
          );
          if (selectedFromData) {
            state.assignedQuestionnaire = selectedFromData;
          }
        }
      }
    );
  }
});

function updateStateWithResponse(
  state: QuestionnaireState,
  resp: QuestionnaireQuestionResponse
) {
  state.loading = false;
  state.totalQuestions = resp.totalQuestions;
  state.status = resp.questionnaireStatus;
  state.group = resp.groupName;
  state.groupTitleId = resp.groupTitleId;
  state.orderIndex = resp.orderIndex;
  state.currentGroupState = undefined;
  state.current =
    resp.questions?.map((q) => ({
      question: q,
      answer: resp.answers?.[q.id]
    })) ?? null;
}

export const {
  setCurrentAssignedQuestionnaire,
  setLoading,
  setCurrentQuestion,
  updateGroupMemberState,
  setQuestionnaireStatus,
  resetQuestionnaireState,
  resetAndSetQuestionnaire,
  setFixedChoices,
  setInitializationStatus: setQuestionnaireInitializationStatus
} = questionnaireSlice.actions;
export const { reducer: questionnaireReducer } = questionnaireSlice;
